import {
	MarketId,
	UIMarket,
	UIOrderType,
	roundToStepSizeIfLargeEnough,
} from '@drift/common';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import { getSpotLiqPriceStr } from 'src/utils/position';
import { toNotional } from 'src/utils/formatting';
import { useMemo } from 'react';
import {
	useCurrentMarketStepSizeBn,
	useMarketStepSize,
} from 'src/hooks/useMarketStepSize';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import useCurrentMarketMarkPrice from 'src/hooks/useCurrentMarketMarkPrice';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import useTradeFormEstimatedLiquidationPrice from 'src/hooks/Liquidations/useTradeFormEstimatedLiquidationPrice';
import useInfoForCurrentlySelectedMarket from 'src/hooks/useInfoForCurrentlySelectedMarket';
import useAccountData from 'src/hooks/useAccountData';

interface Params {
	marketType: MarketType;
	orderType: UIOrderType;
	tradeSide: 'buy' | 'sell';
	baseSizeStringValue: string;
	quoteSizeStringValue: string;
	priceBoxStringValue: string;
	secondaryPriceBoxStringValue: string;
	selectedMarketId: MarketId;
}

/**
 * Provides logic shared across perp and spot trade confirmation modals.
 */
export default function useTradeConfirmationModal({
	marketType,
	orderType,
	tradeSide,
	baseSizeStringValue,
	quoteSizeStringValue,
	priceBoxStringValue,
	secondaryPriceBoxStringValue,
	selectedMarketId,
}: Params) {
	const [sizeUnit] = useSizeInputSource();

	const user = useAccountData()?.client;

	const markPrice = useCurrentMarketMarkPrice();
	const estimatedLiquidationPrice = useTradeFormEstimatedLiquidationPrice();

	const selectedMarket = useMemo(
		() => UIMarket.fromMarketId(selectedMarketId),
		[selectedMarketId]
	);

	const tradeCurrency = selectedMarket.baseAssetSymbol();
	const marketSymbol = selectedMarket.baseAssetSymbol();

	const stepSize = useMarketStepSize(selectedMarketId);
	const currentStepSize = useCurrentMarketStepSizeBn();
	const stepSizeDecimalPlaces = (`${currentStepSize}`.split('.')[1] ?? '')
		.length;

	const tickSizeDecimalPlaces =
		useInfoForCurrentlySelectedMarket().info?.genericInfo
			?.priceDisplayDecimals ?? 2;

	const baseSize = BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP);
	const quoteSize = BigNum.fromPrint(quoteSizeStringValue, QUOTE_PRECISION_EXP);
	const priceBoxValue = Number(priceBoxStringValue);
	const secondaryPriceBoxValue = Number(secondaryPriceBoxStringValue);

	const baseSizeDisplayString = roundToStepSizeIfLargeEnough(
		baseSizeStringValue,
		stepSize
	);

	const isPerp = marketType === MarketType.PERP;

	const { actionValue, actionValueClassName } = (() => {
		if (tradeSide === 'buy') {
			return {
				actionValue: `Buy${isPerp ? '/Long' : ''} ${marketSymbol}`,
				actionValueClassName: 'text-text-positive-green-button',
			};
		}

		return {
			actionValue: `Sell${isPerp ? '/Short' : ''} ${marketSymbol}`,
			actionValueClassName: 'text-text-negative-red-button',
		};
	})();

	const { sizeDisplayLabel, sizeDisplayValue } = (() => {
		if (sizeUnit === 'base') {
			return {
				sizeDisplayLabel: 'Size',
				sizeDisplayValue: `${baseSizeDisplayString} ${tradeCurrency}`,
			};
		}

		return {
			sizeDisplayLabel: 'Value',
			sizeDisplayValue:
				orderType !== 'market'
					? quoteSize.toNotional()
					: markPrice.scalarMul(baseSize).toNotional(),
		};
	})();

	const estimatedLiquidationPriceUi = useMemo(() => {
		if (isPerp) {
			if (estimatedLiquidationPrice <= 0) {
				return PRICE_FALLBACK_DISPLAY_VALUE;
			}

			const decimals = getMarketPriceDecimals(marketSymbol);
			return toNotional(estimatedLiquidationPrice, decimals);
		}

		if (!user) {
			return PRICE_FALLBACK_DISPLAY_VALUE;
		}

		return getSpotLiqPriceStr(
			user,
			selectedMarketId.marketIndex,
			baseSize,
			tradeSide === 'buy'
		);
	}, [
		estimatedLiquidationPrice,
		isPerp,
		user,
		selectedMarketId.marketIndex,
		baseSize,
		tradeSide,
		marketSymbol,
	]);

	return {
		selectedMarket,
		actionValue,
		actionValueClassName,
		sizeDisplayLabel,
		sizeDisplayValue,
		priceBoxValue,
		secondaryPriceBoxValue,
		estimatedLiquidationPriceUi,
		tickSizeDecimalPlaces,
		stepSizeDecimalPlaces,
		currentStepSize,
	};
}
